import BroadcasterLimitReachedModal from 'features/room/modals/BroadcasterLimitReachedModal';
import ScreenshareLimitReachedModal from 'features/room/modals/ScreenshareLimitReachedModal';
import React, { Suspense, useMemo } from 'react';
import RoomSettingsModal from 'features/room/settings/RoomSettingsModal';
import ScreensharePermissionsError from 'features/room/modals/ScreensharePermissionsError';
import BlockedDevicesModal from 'features/room/modals/BlockedDevicesModal';
import MuteRemoteAudioConfirmation from 'features/room/modals/MuteRemoteAudioConfirmation';
import { useAppSelector } from 'store/hooks';
import { selectActiveModal } from 'features/modal/modalSlice';
import EndSessionConfirmation from 'features/room/modals/EndSessionConfirmation';
import UnmuteRemoteAudioConfirmation from 'features/room/modals/UnmuteRemoteAudioConfirmation';
import MuteRemoteVideoConfirmation from 'features/room/modals/MuteRemoteVideoConfirmation';
import UnmuteRemoteVideoConfirmation from 'features/room/modals/UnmuteRemoteVideoConfirmation';
import MediaConnectionFailedModal from 'features/room/modals/MediaConnectionFailedModal';
import { ModalName } from 'features/modal/types';
import RecordingConsentModal from 'features/recorder/modals/RecordingConsentModal';
import RecordingFailureModal from 'features/recorder/modals/RecordingFailureModal';
import RecordingFailureRuntimeModal from 'features/recorder/modals/RecordingFailureRuntimeModal';
import StopRecordingConfirmation from 'features/recorder/modals/StopRecordingConfirmation';
import KickUserConfirmation from 'features/room/modals/KickUserConfirmation';
import KickUsersConfirmation from 'features/room/modals/KickUsersConfirmation';
import ManageRole from 'features/room/modals/ManageRole';
import RoomSecurityCodeModal from 'features/room/modals/RoomSecurityCodeModal';
import InviteUserModal from 'features/users/modals/invite-user/InviteUserModal';
import ClearActionNotificationsConfirmation from 'features/room/modals/ClearActionNotificationsConfirmation';
import LockRoomConfirmation from 'features/room/modals/LockRoomConfirmation';
import UnLockRoomConfirmation from 'features/room/modals/UnLockRoomConfirmation';
import RecordingE2eeConsentModal from 'features/recorder/modals/RecordingE2eeConsentModal';
import RecordingPlanLengthLimitModal from 'features/recorder/modals/RecordingPlanLengthLimitModal';
import RecordingFreeTotalLimitModal from 'features/recorder/modals/RecordingFreeTotalLimitModal';
import AdmitAllWaitingParticipants from 'features/users/modals/AdmitAllWaitingParticipants';
import RejectAllWaitingParticipants from 'features/users/modals/RejectAllWaitingParticipants';
import BulkMuteRemoteAudioConfirmation from 'features/room/modals/BulkMuteRemoteAudioConfirmation';
import BulkMuteRemoteVideoConfirmation from 'features/room/modals/BulkMuteRemoteVideoConfirmation';
import CreateBreakoutRoomsModal from 'features/breakout-rooms/modals/CreateBreakoutRoomsModal';
import EditBreakoutRoomsSettingsModal from 'features/breakout-rooms/modals/EditBreakoutRoomsSettingsModal';
import DeleteBreakoutRoomConfirmation from 'features/breakout-rooms/modals/DeleteBreakoutRoomConfirmation';
import RenameBreakoutRoomModal from 'features/breakout-rooms/modals/RenameBreakoutRoomModal';
import ClearBreakoutRoomsConfirmation from 'features/breakout-rooms/modals/ClearBreakoutRoomsConfirmation';
import AssignUserToRoomModal from 'features/breakout-rooms/modals/AssignUserToRoomModal';
import MoveUserToAnotherRoomModal from 'features/breakout-rooms/modals/MoveUserToAnotherRoomModal';
import AddUsersToRoomModal from 'features/breakout-rooms/modals/AddUsersToRoomModal';
import DeleteQuestionConfirmation from 'features/qa/modals/DeleteQuestionConfirmation';
import DeleteAnswerConfirmation from 'features/qa/modals/DeleteAnswerConfirmation';
import CreatePollModal from 'features/polls/modals/CreatePollModal';
import EditPollModal from 'features/polls/modals/EditPollModal';
import DeletePollConfirmation from 'features/polls/modals/DeletePollConfirmation';
import CreatePollTemplateModal from 'features/polls/modals/CreatePollTemplateModal';
import EditPollTemplateModal from 'features/polls/modals/EditPollTemplateModal';
import DeletePollTemplateConfirmation from 'features/polls/modals/DeletePollTemplateConfirmation';
import CreateQuizModal from 'features/quizzes/modals/CreateQuizModal';
import EditQuizModal from 'features/quizzes/modals/EditQuizModal';
import DeleteQuizConfirmation from 'features/quizzes/modals/DeleteQuizConfirmation';
import CreateQuizTemplateModal from 'features/quizzes/modals/CreateQuizTemplateModal';
import EditQuizTemplateModal from 'features/quizzes/modals/EditQuizTemplateModal';
import DeleteQuizTemplateConfirmation from 'features/quizzes/modals/DeleteQuizTemplateConfirmation';
import StartTranscriptionConfirmation from 'features/transcripts/modals/StartTranscriptionConfirmation';
import CreateContentLibraryFolderModal from 'features/content-library/modals/CreateContentLibraryFolderModal';
import RenameContentLibraryFolderModal from 'features/content-library/modals/RenameContentLibraryFolderModal';
import RenameContentLibraryFileModal from 'features/content-library/modals/RenameContentLibraryFileModal';
import DeleteContentLibraryFileConfirmation from 'features/content-library/modals/DeleteContentLibraryFileConfirmation';
import DeleteContentLibraryFolderConfirmation from 'features/content-library/modals/DeleteContentLibraryFolderConfirmation';
import CreateEmbedContentLibraryContentModal from 'features/content-library/modals/CreateEmbedContentLibraryContentModal';
import CreateNewWhiteboardConfirmationModal from 'features/whiteboard/modals/CreateNewWhiteboardConfirmation';
import WhiteboardsModal from 'features/whiteboard/modals/WhiteboardsModal';
import CloseWhiteboardConfirmationModal from 'features/whiteboard/modals/CloseWhiteboardConfirmationModal';
import CloseRestorableWhiteboardModal from 'features/whiteboard/modals/CloseRestorableWhiteboardModal';
import BulkCloseContentConfirmationModal from 'features/layout/modals/BulkCloseContentConfirmationModal';
import WhiteboardAlreadyOpenedModal from 'features/whiteboard/modals/WhiteboardAlreadyOpenedModal';
import MakeWhiteboardPublicConfirmationModal from 'features/whiteboard/modals/MakeWhiteboardPublicConfirmationModal';
import MakeWhiteboardPrivateConfirmationModal from 'features/whiteboard/modals/MakeWhiteboardPrivateConfirmationModal';
import SyncWhiteboardThemeModal from 'features/whiteboard/modals/SyncWhiteboardThemeModal';
import ContentLibraryE2eeWBWarningModal from 'features/content-library/modals/ContentLibraryE2eeWBWarningModal';
import ShareContentLibraryFileModal from 'features/content-library/modals/ShareContentLibraryFileModal';
import MoveContentLibraryItemModal from 'features/content-library/modals/MoveContentLibraryItemModal';
import PhoneConnectionModal from 'features/sip/modals/PhoneConnectionModal';
import MuteRemotePhoneAudioConfirmation from 'features/sip/modals/MuteRemotePhoneAudioConfirmation';
import UnmuteRemotePhoneAudioConfirmation from 'features/sip/modals/UnmuteRemotePhoneAudioConfirmation';
import BanUserConfirmation from 'features/room/modals/BanUserConfirmation';
import AssignWaitingUserToRoomModal from 'features/breakout-rooms/modals/AssignWaitingUserToRoomModal';
import ClearContentConfirmation from 'features/room/modals/ClearContentConfirmation';
import ClearPublicChatConfirmation from 'features/chat/modals/ClearPublicChatConfirmation';
import ClearContentLibraryConfirmation from 'features/content-library/modals/ClearContentLibraryConfirmation';
import AddBookmarkModal from 'features/recorder/modals/AddBookmarkModal';
import RenameUserModal from 'features/users/modals/RenameUserModal';
import RestreamingConfigModal from 'features/restreamer/modals/RestreamingConfigModal';
import RestreamingConsentModal from 'features/restreamer/modals/RestreamingConsentModal';
import RestreamingE2eeConsentModal from 'features/restreamer/modals/RestreamingE2eeConsentModal';
import RestreamingFailureModal from 'features/restreamer/modals/RestreamingFailureModal';
import RestreamingFailureRuntimeModal from 'features/restreamer/modals/RestreamingFailureRuntimeModal';
import StopRestreamingConfirmation from 'features/restreamer/modals/StopRestreamingConfirmation';
import RoomReactionsModal from 'features/room-reactions/modals/RoomReactionsModal';
import LockContentConfirmation from 'features/layout/features/content/modals/LockContentConfirmation';
import PushLockedContentConfirmation from 'features/layout/features/content/modals/PushLockedContentConfirmation';
import ContentLockedByModeratorModal from 'features/layout/features/content/modals/ContentLockedByModeratorModal';

const componentsMap: Partial<Record<ModalName, React.ComponentType>> = {
  roomSettings: RoomSettingsModal,
  screensharePermissionsError: ScreensharePermissionsError,
  mediaDevicesBlocked: BlockedDevicesModal,
  muteRemoteAudioConfirmation: MuteRemoteAudioConfirmation,
  muteRemoteVideoConfirmation: MuteRemoteVideoConfirmation,
  unmuteRemoteAudioConfirmation: UnmuteRemoteAudioConfirmation,
  unmuteRemoteVideoConfirmation: UnmuteRemoteVideoConfirmation,
  endSessionConfirm: EndSessionConfirmation,
  mediaConnectionFailed: MediaConnectionFailedModal,
  recordingConsent: RecordingConsentModal,
  recordingE2eeConsent: RecordingE2eeConsentModal,
  recordingPlanLengthLimit: RecordingPlanLengthLimitModal,
  recordingFreePlanTotalLimit: RecordingFreeTotalLimitModal,
  createNewWhiteboardConfirmation: CreateNewWhiteboardConfirmationModal,
  whiteboards: WhiteboardsModal,
  closeWhiteboardConfirmation: CloseWhiteboardConfirmationModal,
  closeRestorableWhiteboard: CloseRestorableWhiteboardModal,
  bulkCloseContentConfirmation: BulkCloseContentConfirmationModal,
  whiteboardAlreadyOpened: WhiteboardAlreadyOpenedModal,
  recordingFailure: RecordingFailureModal,
  recordingFailureRuntime: RecordingFailureRuntimeModal,
  stopRecordingConfirmation: StopRecordingConfirmation,
  kickUserConfirmation: KickUserConfirmation,
  kickUsersConfirmation: KickUsersConfirmation,
  manageRole: ManageRole,
  roomE2eeCode: RoomSecurityCodeModal,
  inviteUser: InviteUserModal,
  clearActionNotificationsConfirmation: ClearActionNotificationsConfirmation,
  lockRoomConfirmation: LockRoomConfirmation,
  unlockRoomConfirmation: UnLockRoomConfirmation,
  admitAllWaitingParticipants: AdmitAllWaitingParticipants,
  rejectAllWaitingParticipants: RejectAllWaitingParticipants,
  broadcasterLimitReached: BroadcasterLimitReachedModal,
  screenshareLimitReached: ScreenshareLimitReachedModal,
  bulkMuteRemoteAudioConfirmation: BulkMuteRemoteAudioConfirmation,
  bulkMuteRemoteVideoConfirmation: BulkMuteRemoteVideoConfirmation,
  createBreakoutRooms: CreateBreakoutRoomsModal,
  editBreakoutRoomsSettings: EditBreakoutRoomsSettingsModal,
  deleteBreakoutRoom: DeleteBreakoutRoomConfirmation,
  renameBreakoutRoom: RenameBreakoutRoomModal,
  clearBreakoutRooms: ClearBreakoutRoomsConfirmation,
  assignUserToBreakoutRoom: AssignUserToRoomModal,
  moveUserToAnotherBreakoutRoom: MoveUserToAnotherRoomModal,
  addUsersToBreakoutRoom: AddUsersToRoomModal,
  deleteQuestion: DeleteQuestionConfirmation,
  deleteAnswer: DeleteAnswerConfirmation,
  createPoll: CreatePollModal,
  editPoll: EditPollModal,
  deletePoll: DeletePollConfirmation,
  createPollTemplate: CreatePollTemplateModal,
  editPollTemplate: EditPollTemplateModal,
  deletePollTemplate: DeletePollTemplateConfirmation,
  createQuiz: CreateQuizModal,
  editQuiz: EditQuizModal,
  deleteQuiz: DeleteQuizConfirmation,
  createQuizTemplate: CreateQuizTemplateModal,
  editQuizTemplate: EditQuizTemplateModal,
  deleteQuizTemplate: DeleteQuizTemplateConfirmation,
  startTranscriptionConfirmation: StartTranscriptionConfirmation,
  createContentLibraryFolder: CreateContentLibraryFolderModal,
  renameContentLibraryFolder: RenameContentLibraryFolderModal,
  renameContentLibraryFile: RenameContentLibraryFileModal,
  deleteContentLibraryFileConfirmation: DeleteContentLibraryFileConfirmation,
  deleteContentLibraryFolderConfirmation: DeleteContentLibraryFolderConfirmation,
  createEmbedContentLibraryContent: CreateEmbedContentLibraryContentModal,
  contentLibraryE2EEWhiteboardWarning: ContentLibraryE2eeWBWarningModal,
  shareContentLibraryFile: ShareContentLibraryFileModal,
  moveContentLibraryItem: MoveContentLibraryItemModal,
  phoneConnection: PhoneConnectionModal,
  muteRemotePhoneAudioConfirmation: MuteRemotePhoneAudioConfirmation,
  unmuteRemotePhoneAudioConfirmation: UnmuteRemotePhoneAudioConfirmation,
  banUserConfirmation: BanUserConfirmation,
  assignWaitingUserToBreakoutRoom: AssignWaitingUserToRoomModal,
  makeWhiteboardPublicConfirmation: MakeWhiteboardPublicConfirmationModal,
  makeWhiteboardPrivateConfirmation: MakeWhiteboardPrivateConfirmationModal,
  syncWhiteboardTheme: SyncWhiteboardThemeModal,
  clearContentConfirmation: ClearContentConfirmation,
  clearPublicChatConfirmation: ClearPublicChatConfirmation,
  clearContentLibraryConfirmation: ClearContentLibraryConfirmation,
  addBookmarkModal: AddBookmarkModal,
  renameUser: RenameUserModal,
  restreamingConfig: RestreamingConfigModal,
  restreamingConsent: RestreamingConsentModal,
  restreamingE2eeConsent: RestreamingE2eeConsentModal,
  restreamingFailure: RestreamingFailureModal,
  restreamingFailureRuntime: RestreamingFailureRuntimeModal,
  stopRestreamingConfirmation: StopRestreamingConfirmation,
  roomReactions: RoomReactionsModal,
  lockContentConfirm: LockContentConfirmation,
  pushLockedContentConfirm: PushLockedContentConfirmation,
  contentLockedByModerator: ContentLockedByModeratorModal,
};

const RoomModalManager = () => {
  const { name, data: props } = useAppSelector(selectActiveModal);

  const Modal = useMemo(() => {
    if (!name) {
      return null;
    }

    return componentsMap[name];
  }, [name]);

  if (!Modal) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <Modal {...props} />
    </Suspense>
  );
};

export default RoomModalManager;
